import { ReactNode, useEffect, useState } from "react";

import { Button } from "@runwayhealth/runway-components-react";
import { Case } from "@types-runway/case";
import { PaymentOption, PaymentOptionKind } from "@types-runway/paymentRetry";
import { formatCentsAsDollars } from "@utils/billing/formatAmount";

import OrderSummary from "./OrderSummary";
import "./paymentPanel.scss";

const optionLabels: Record<PaymentOptionKind, string> = {
  [PaymentOptionKind.PAY_BY_CARD]: "Pay by card",
  [PaymentOptionKind.RETRY_HSA_FSA]: "Retry HSA/FSA",
};

const optionRunningLabels: Record<PaymentOptionKind, string> = {
  [PaymentOptionKind.PAY_BY_CARD]: "Preparing payment…",
  [PaymentOptionKind.RETRY_HSA_FSA]: "Retrying HSA/FSA…",
};

const optionHints: Record<PaymentOptionKind, string> = {
  [PaymentOptionKind.PAY_BY_CARD]: "Uses your saved default card.",
  [PaymentOptionKind.RETRY_HSA_FSA]: "Works only if funds are back in your account.",
};

interface PaymentPanelProps {
  case_: Case;
  isPending: boolean;
  options: PaymentOption[];
  banner: ReactNode;
  singleActionLabel: string;
  onPay: (option: PaymentOption) => void;
  isAwaitingConfirmation: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

interface CardPaymentReviewProps {
  case_: Case;
  isPending: boolean;
  banner: ReactNode;
  onConfirm: () => void;
  onCancel: () => void;
}

// The amount of the Stripe invoice just created; nothing is charged until the patient confirms.
const CardPaymentReview = ({
  case_,
  isPending,
  banner,
  onConfirm,
  onCancel,
}: CardPaymentReviewProps) => {
  const headingId = `payment-review-${case_.id}`;
  const amount = formatCentsAsDollars(case_.CurexaOrder.invoice?.amount ?? 0);

  return (
    <section className="payment-panel" aria-labelledby={headingId} data-testid="payment-panel">
      <h3 className="payment-panel-title" id={headingId}>
        Review your payment
      </h3>
      {banner}
      <OrderSummary case_={case_} />
      <p className="payment-review-note">
        You&apos;ll be charged {amount} on your saved default card.
      </p>
      <div className="payment-actions">
        <div className="payment-action">
          <Button
            size="md"
            variant="primary"
            onClick={onConfirm}
            isLoading={isPending}
            disabled={isPending}
          >
            {isPending ? "Processing payment" : `Pay ${amount}`}
          </Button>
        </div>
        <div className="payment-action">
          <Button size="md" variant="outline" onClick={onCancel} disabled={isPending}>
            Cancel
          </Button>
        </div>
      </div>
    </section>
  );
};

export const PaymentPanel = ({
  case_,
  isPending,
  options,
  banner,
  singleActionLabel,
  onPay,
  isAwaitingConfirmation,
  onConfirm,
  onCancel,
}: PaymentPanelProps) => {
  const [attemptedKind, setAttemptedKind] = useState<PaymentOptionKind | null>(null);
  const headingId = `payment-panel-${case_.id}`;

  useEffect(() => {
    if (!isPending) {
      setAttemptedKind(null);
    }
  }, [isPending]);

  const pay = (option: PaymentOption) => {
    setAttemptedKind(option.kind);
    onPay(option);
  };

  if (isAwaitingConfirmation) {
    return (
      <CardPaymentReview
        case_={case_}
        isPending={isPending}
        banner={banner}
        onConfirm={onConfirm}
        onCancel={onCancel}
      />
    );
  }

  return (
    <section className="payment-panel" aria-labelledby={headingId} data-testid="payment-panel">
      <h3 className="payment-panel-title" id={headingId}>
        Payment not completed
      </h3>
      {banner}
      <OrderSummary case_={case_} />
      <div className="payment-actions">
        {options.length > 1 ? (
          options.map((option, index) => {
            const isRunning = isPending && attemptedKind === option.kind;
            const hintId = `payment-hint-${case_.id}-${option.kind}`;

            return (
              <div className="payment-action" key={option.kind}>
                <Button
                  size="md"
                  variant={index === 0 ? "primary" : "outline"}
                  onClick={() => pay(option)}
                  isLoading={isRunning}
                  disabled={isPending && !isRunning}
                  aria-describedby={hintId}
                >
                  {isRunning ? optionRunningLabels[option.kind] : optionLabels[option.kind]}
                </Button>
                <p className="payment-action-hint" id={hintId}>
                  {optionHints[option.kind]}
                </p>
              </div>
            );
          })
        ) : (
          <div className="payment-action">
            <Button
              size="md"
              onClick={() => pay(options[0])}
              iconName="ArrowRight"
              isLoading={isPending}
              disabled={isPending}
            >
              {isPending ? "Processing payment" : singleActionLabel}
            </Button>
          </div>
        )}
      </div>
    </section>
  );
};
